import './sidebar.scss';

import {
  CCreateElement,
  CImg,
  CSidebar,
  CSidebarBrand,
  CSidebarMinimizer,
  CSidebarNav,
  CSidebarNavDropdown,
  CSidebarNavItem,
} from '@coreui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { useQueryUserMyPermissions } from '../../../hooks/useUserQuery';
import { routeConfig } from '../../../navigation/routeConfig';

interface ISidebarProps {
  sidebarState: '' | true | false | 'responsive';
  onToggle: (show: '' | true | false | 'responsive') => void;
}

export const Sidebar: React.FunctionComponent<ISidebarProps> = ({ sidebarState, onToggle }) => {
  const { loading, data } = useQueryUserMyPermissions();
  const { t } = useTranslation();
  const flags = useFlags();
  const [,, removeSessionValue] = useSessionStorage('formData');
  const items: any = [
    {
      _tag: 'CSidebarNavItem',
      name: t('sidebar.dashboard'),
      to: routeConfig.dashboard.path,
      icon: 'cil-speedometer',
    },
  ];

  if (data?.myPermissions.includes('GLOBAL_READ_USERS') || data?.myPermissions.includes('GLOBAL_READ_ORGS')) {
    const accounts: any = {
      _tag: 'CSidebarNavDropdown',
      name: t('sidebar.accounts'),
      to: routeConfig.users.path,
      icon: 'cil-address-book',
      _children: [],
    };
    if (data?.myPermissions.includes('GLOBAL_READ_USERS')) {
      accounts._children.push({
        _tag: 'CSidebarNavItem',
        name: t('sidebar.users'),
        to: routeConfig.users.path,
      });
    }
    if (data?.myPermissions.includes('GLOBAL_READ_ORGS')) {
      accounts._children.push({
        _tag: 'CSidebarNavItem',
        name: t('sidebar.organizations'),
        to: routeConfig.organizations.path,
      });
    }
    items.push(accounts);
  }

  if (data?.myPermissions.includes('GLOBAL_READ_BOOKINGS')) {
    items.push(
      {
        _tag: 'CSidebarNavDropdown',
        name: t('sidebar.bookings'),
        to: routeConfig.bookingMain.path,
        icon: 'cil-address-book',
        _children: [
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.viewBookings'),
            to: routeConfig.bookingMain.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.viewJourneys'),
            to: routeConfig.journeyMain.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.createBooking'),
            to: routeConfig.bookingCreation.path,
            onClick: () => removeSessionValue(),
          },
          flags?.awaitingApproval && {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.viewAwaitingApprovalJourneys'),
            to: routeConfig.awaitingApprovalJourneys.path,
          },
          flags?.pendingJourneys && {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.viewPendingJourneys'),
            to: routeConfig.pendingJourneyMain.path,
          },
        ],
      },
    );
  }

  if (data?.myPermissions.includes('GLOBAL_READ_HUBS')) {
    items.push(
      {
        _tag: 'CSidebarNavDropdown',
        name: t('sidebar.hubs'),
        to: routeConfig.hubs.path,
        icon: 'cilAirplaneMode',
        _children: [
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.hubs'),
            to: routeConfig.hubs.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.serviceBlocks'),
            to: routeConfig.serviceBlockMain.path,
          },
        ],
      },
    );
  }

  if (data?.myPermissions.includes('GLOBAL_READ_NON_SENSITIVE')) {
    items.push(
      {
        _tag: 'CSidebarNavDropdown',
        name: t('sidebar.exchangeRates'),
        to: routeConfig.exchangeRates.path,
        icon: 'cil-spreadsheet',
        _children: [
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.viewExchangeRates'),
            to: routeConfig.exchangeRates.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.currencyCalculator'),
            to: routeConfig.exchangeRates.path,
          },
        ],
      },
    );
  }

  if (data?.myPermissions.includes('GLOBAL_READ_TRANSCOS')) {
    items.push(
      {
        _tag: 'CSidebarNavDropdown',
        name: t('sidebar.transferCompanies'),
        to: routeConfig.transferCompanies.path,
        icon: 'cilTransfer',
        _children: [
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.transferCompanies'),
            to: routeConfig.transferCompanies.path,
          },
        ],
      },
    );
  }

  if (data?.myPermissions.includes('GLOBAL_READ_PARTNERS')) {
    items.push(
      {
        _tag: 'CSidebarNavDropdown',
        name: t('sidebar.partners'),
        to: routeConfig.partners.path,
        icon: 'cilBadge',
        _children: [
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.viewPartners'),
            to: routeConfig.partners.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.geoZones'),
            to: routeConfig.geoZoneMain.path,
          },
          flags?.showReviewFileUpload && {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.uploadReviews'),
            to: routeConfig.reviewFile.path,
          },
        ],
      },
    );
  }

  if (data?.myPermissions.includes('GLOBAL_READ_INVOICES')) {
    items.push(
      {
        _tag: 'CSidebarNavDropdown',
        name: t('sidebar.finance'),
        icon: 'cilCalculator',
        _children: [
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.billableJourneys'),
            to: routeConfig.billableJourneyMain.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.transcoInvoices'),
            to: routeConfig.transferCompanyInvoiceMain.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.partnerInvoices'),
            to: routeConfig.partnerInvoiceMain.path,
          },
          {
            _tag: 'CSidebarNavItem',
            name: t('sidebar.manualAdjustments'),
            to: routeConfig.manualAdjustmentMain.path,
          },
        ],
      },
    );
  }

  // if (data?.myPermissions.includes('GLOBAL_READ_AUDIT_LOGS')) {
  items.push(
    {
      _tag: 'CSidebarNavDropdown',
      name: t('sidebar.system'),
      icon: 'cilDescription',
      _children: [
        data?.myPermissions.includes('GLOBAL_READ_AUDIT_LOGS') && {
          _tag: 'CSidebarNavItem',
          name: t('sidebar.logs'),
          to: routeConfig.logMain.path,
        },
        data?.myPermissions.includes('GLOBAL_VIEW_SENT_EMAIL') && {
          _tag: 'CSidebarNavItem',
          name: t('sidebar.sentEmails'),
          to: routeConfig.sentEmails.path,
        },
        flags?.showMaintenanceWindow && data?.myPermissions.includes('GLOBAL_WRITE_MAINTENANCE_WINDOWS') && {
          _tag: 'CSidebarNavItem',
          name: t('sidebar.maintenanceWindow'),
          to: routeConfig.maintenanceWindow.path,
        },
        data?.myPermissions.includes('GLOBAL_READ_QUOTES') && {
          _tag: 'CSidebarNavItem',
          name: t('sidebar.quoteRequests'),
          to: routeConfig.quoteRequests.path,
        },
        data?.myPermissions.includes('GLOBAL_READ_BOOKINGS') && {
          _tag: 'CSidebarNavItem',
          name: t('sidebar.webhookEvents'),
          to: routeConfig.webhookEvents.path,
        },
      ],
    },
  );
  // }

  return (
    <CSidebar
      show={sidebarState}
      unfoldable
      onShowChange={onToggle}
    >
      <CSidebarBrand tag="a" className="d-md-down-none" to={routeConfig.dashboard.path}>
        <CImg src="/logo-secondary.png" alt="logo" className="logo c-sidebar-brand-full" />
        <CImg src="/logo-small.png" alt="logo" className="logoSmall c-sidebar-brand-minimized" />
      </CSidebarBrand>
      <CSidebarNav>
        {!loading && <CCreateElement
          items={items}
          components={{
            CSidebarNavDropdown,
            CSidebarNavItem,
          }}
        />
        }
      </CSidebarNav>
      <CSidebarMinimizer />
    </CSidebar>
  );
};
